import { useState, useEffect } from "react";
import ChecklistItem from "./components/ChecklistItem";

const STORAGE_KEY = "fpc_china_biz_visa_checklist";

const ITEMS = [
  {
    number: 1,
    title: "Complete the COVA Online Application",
    body: (
      <>
        <p>Complete your visa application on the official Chinese Consulate <a href="https://consular.mfa.gov.cn/VISA/" target="_blank" rel="noopener noreferrer" className="text-[#1A365D] underline hover:no-underline">COVA System Portal</a>. Register an account with your email and verify it.</p>
        <p>On the first screen choose <strong>English</strong>, <strong>North America</strong>, and the consulate covering your state. When prompted for visa type, select <strong>M – Business/Trade</strong>.</p>
        <p>The COVA form has 9 detailed sections. Fill every section completely. Key tips:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Section 1 – Personal Information:</strong> Enter your name and passport details <strong>exactly</strong> as they appear on your passport. Include middle name in the Given Name field if your passport lists one.</li>
          <li><strong>Section 2 – Visa Type:</strong> Select "M – Business/Trade".</li>
          <li><strong>Section 3 – Work Information:</strong> Provide at least 5 years of work history. Do not leave job position or duties blank.</li>
          <li><strong>Sections 4–6 – Education &amp; Family:</strong> Fill in educational background and immediate family details. For National ID, use your driver's license number — do <strong>not</strong> use your SSN.</li>
          <li><strong>Section 7 – Travel Information:</strong> Enter intended entry dates, itinerary, and accommodation details.</li>
        </ul>
        <div className="bg-amber-50 border-l-4 border-amber-400 px-4 py-3 rounded text-amber-800">
          <strong>Important:</strong> Once submitted online, <strong>no information can be changed</strong>. Double-check every field before submitting — if anything needs fixing, the entire form must be redone.
        </div>
        <p>When finished, print the full COVA form set (8–10 pages) and hand-sign and date the <strong>Confirmation Page</strong> AND <strong>section 9.1A on page 7</strong>.</p>
        <div className="bg-amber-50 border-l-4 border-amber-400 px-4 py-3 rounded text-amber-800">
          <strong>Critical — do not skip:</strong> Make sure the printed pages include the <strong>Confirmation Page with the large barcode</strong>. This is the page the consulate officer scans at the submission window. Without the barcode page, your submission will be turned away.
        </div>
      </>
    ),
  },
  {
    number: 2,
    title: "Valid U.S. Passport & Photocopy",
    body: (
      <>
        <p>Send your <strong>actual passport</strong> along with a photocopy of the main photo and data page.</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Passport must have more than <strong>6 months of validity</strong> remaining</li>
          <li>Passport must have at least <strong>one blank visa page</strong></li>
          <li>Passport must be in <strong>good condition</strong> with no damage</li>
        </ul>
      </>
    ),
  },
  {
    number: 3,
    title: "Photocopy of Valid Photo Identification",
    body: (
      <>
        <p>One clear <strong>color copy of both the front and back</strong> of your valid Driver's License, State ID, Military ID, or valid Passport.</p>
        <p>If your name has changed from your citizenship document, also provide the original marriage certificate, divorce decree, or court-issued name change document.</p>
      </>
    ),
  },
  {
    number: 4,
    title: "Two New Passport Photos",
    body: (
      <>
        <p>Get two brand-new passport photos at your local Staples, CVS, Rite Aid, Walgreens, or similar.</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Photos must be <strong>under 6 months old</strong> and different from the photo in your current passport</li>
          <li>Color photo with a <strong>plain white background</strong>, no shadows</li>
          <li><strong>No hat or glasses</strong> — the consulate will reject photos that include them</li>
          <li>You will also upload a digital scan of this photo into the COVA form</li>
        </ul>
        <div className="bg-amber-50 border-l-4 border-amber-400 px-4 py-3 rounded text-amber-800">
          <strong>Easy mistake to avoid:</strong> Do not wear a white shirt for your photo. With a white background, white clothing can blur the face outline and cause rejection.
        </div>
      </>
    ),
  },
  {
    number: 5,
    title: "Photocopy of Your Most Recent China Visa",
    tag: "(if applicable)",
    body: (
      <>
        <p>If this is not your first China visa application, you must provide a copy of the most recent China visa issued to you.</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>If the visa is in an <strong>old passport</strong>, submit that old passport along with your current one</li>
          <li>If you <strong>no longer have</strong> the old passport, provide a signed statement explaining what happened to it (lost, expired and discarded, etc.)</li>
        </ul>
      </>
    ),
  },
  {
    number: 6,
    title: "Non-U.S. Citizens & Naturalized U.S. Citizens",
    body: (
      <>
        <p>This step applies if you are not a natural-born U.S. citizen.</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Color photocopy of your <strong>Naturalization Certificate, valid Green Card, U.S. Visa, I-20, or I-94</strong> document</li>
          <li>If you were <strong>previously a Chinese national</strong> and this is your first Chinese visa application after naturalization, you must also provide a photocopy of the bio-page of your old Chinese passport, AND submit your last physical Chinese passport with the application</li>
        </ul>
      </>
    ),
  },
  {
    number: 7,
    title: "Proof of Residence Form",
    body: (
      <>
        <p>Download the <strong>Proof of Residence Form</strong> from your Fast Passport Center order confirmation email. Print it out and complete in <strong>black ink</strong>.</p>
        <p>Sign and include the form in your shipment.</p>
      </>
    ),
  },
  {
    number: 8,
    title: "China Visa Application Consent Form",
    body: (
      <>
        <p>Download the <strong>China Visa Application Consent Form</strong> from your Fast Passport Center order confirmation email. Print it out and complete in <strong>black ink</strong>.</p>
        <p>Sign and include the form in your shipment.</p>
      </>
    ),
  },
  {
    number: 9,
    title: "Proof of Round-Trip Travel",
    body: (
      <>
        <p>Include both:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>One clear photocopy of a <strong>round-trip airline ticket confirmation</strong> with your name listed as the passenger</li>
          <li>One clear photocopy of your <strong>confirmed hotel reservation</strong> from a Chinese hotel with your name listed</li>
        </ul>
        <p>Both can come from a travel agent but must include all flight and hotel details.</p>
      </>
    ),
  },
  {
    number: 10,
    title: "Letter of Invitation from China",
    body: (
      <>
        <p>This letter is typically scanned and emailed to you from the inviting organization in China. <strong>Print it in good color quality</strong> and include it in your shipment.</p>
        <p>The invitation letter must:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Be on the <strong>company letterhead</strong> of the inviting Chinese company, official Chinese government entity, or trade fair organizer</li>
          <li>Be signed by an appropriate person (HR, executive, or business contact)</li>
          <li>Be <strong>stamped with the company seal</strong></li>
          <li>Explain the relationship between you and the inviter, and the purpose of your travel</li>
        </ul>
        <div className="bg-amber-50 border-l-4 border-amber-400 px-4 py-3 rounded text-amber-800">
          <strong>Critical:</strong> Information on the invitation letter must match exactly with your COVA application and other supporting documents. Any mismatch will cause rejection.
        </div>
      </>
    ),
  },
  {
    number: 11,
    title: "Business Cover Letter from Your U.S. Company",
    body: (
      <>
        <p>A letter from your U.S. employer or sponsoring company, on company letterhead, signed by HR, an executive, or your direct supervisor.</p>
        <p>The letter must:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Introduce you and confirm your <strong>employment status and position</strong></li>
          <li>Clearly state the <strong>purpose of your visit</strong> to China</li>
          <li>Include detailed contact information for the organization(s) you will be visiting</li>
        </ul>
        <div className="bg-amber-50 border-l-4 border-amber-400 px-4 py-3 rounded text-amber-800">
          <strong>Critical:</strong> Information on this letter must match exactly with your COVA application and the invitation letter from China.
        </div>
      </>
    ),
  },
  {
    number: 12,
    title: "Print & Include Your Order Confirmation",
    body: (
      <>
        <p>Print the PDF attached to your <strong>Fast Passport Center order confirmation email</strong> and include it in your shipment.</p>
        <p>This PDF includes your order details and the return shipping address we need on file.</p>
      </>
    ),
  },
];

export default function ChinaBusinessVisaChecklistPage() {
  const [checked, setChecked] = useState<boolean[]>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) return JSON.parse(saved);
    } catch {
      // ignore
    }
    return Array(ITEMS.length).fill(false);
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(checked));
  }, [checked]);

  const completedCount = checked.filter(Boolean).length;
  const progressPct = Math.round((completedCount / ITEMS.length) * 100);

  const handleToggle = (i: number) => {
    setChecked((prev) => {
      const next = [...prev];
      next[i] = !next[i];
      return next;
    });
  };

  const handleReset = () => {
    setChecked(Array(ITEMS.length).fill(false));
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <>
      {/* SEO head */}
      <title>China Business Visa Checklist — Fast Passport Center</title>
      <meta
        name="description"
        content="Use Fast Passport Center's interactive China Business Visa (M-Visa) checklist to prepare your application. Track your progress, review all 12 required documents, and call us before shipping."
      />
      <meta name="keywords" content="China business visa checklist, M visa documents, China visa requirements, COVA application, China visa preparation" />
      <link rel="canonical" href="https://fastpassportcenter.com/passport-services/chinese-business-visa-checklist/" />
      {/* ── Structured Data ── */}
      <script
        type="application/ld+json"
        id="structured-data-china-biz-visa-checklist"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "HowTo",
                "name": "China Business Visa (M-Visa) Application Checklist",
                "description": "Complete step-by-step checklist to assemble your China Business Visa application. Track all 12 required documents including COVA form, passport, photos, invitation letter, and business cover letter.",
                "totalTime": "PT2H",
                "supply": [
                  { "@type": "HowToSupply", "name": "Valid U.S. Passport with 6+ months validity" },
                  { "@type": "HowToSupply", "name": "Two passport photos on white background" },
                  { "@type": "HowToSupply", "name": "Printer for COVA form and confirmation pages" },
                ],
                "tool": [
                  { "@type": "HowToTool", "name": "COVA Online Application System" },
                  { "@type": "HowToTool", "name": "Black ink pen for signing forms" },
                ],
                "step": [
                  { "@type": "HowToStep", "position": 1, "name": "Complete the COVA Online Application", "text": "Complete your visa application on the official Chinese Consulate COVA System Portal. Register with email, select English, North America, and your consulate. Choose M – Business/Trade visa type. Fill all 9 sections, print the full form set (8–10 pages), and hand-sign the Confirmation Page and section 9.1A." },
                  { "@type": "HowToStep", "position": 2, "name": "Prepare Valid U.S. Passport & Photocopy", "text": "Send your actual passport along with a photocopy of the main photo and data page. Passport must have 6+ months validity, at least one blank visa page, and be in good condition." },
                  { "@type": "HowToStep", "position": 3, "name": "Photocopy Valid Photo Identification", "text": "Provide one clear color copy of both front and back of your valid Driver's License, State ID, Military ID, or Passport. If name has changed, also provide marriage certificate or court document." },
                  { "@type": "HowToStep", "position": 4, "name": "Get Two New Passport Photos", "text": "Get two brand-new passport photos under 6 months old with plain white background. No hat or glasses allowed. Digital scan must also be uploaded into the COVA form." },
                  { "@type": "HowToStep", "position": 5, "name": "Photocopy Most Recent China Visa (if applicable)", "text": "If not your first China visa, provide a copy of the most recent one. If in an old passport, submit that passport. If you no longer have the old passport, provide a signed statement." },
                  { "@type": "HowToStep", "position": 6, "name": "Non-U.S. Citizen Documentation", "text": "If not a natural-born U.S. citizen, provide color photocopy of Naturalization Certificate, Green Card, U.S. Visa, I-20, or I-94. Former Chinese nationals must also provide old Chinese passport." },
                  { "@type": "HowToStep", "position": 7, "name": "Complete Proof of Residence Form", "text": "Download the Proof of Residence Form from your order confirmation email. Print and complete in black ink. Sign and include in your shipment." },
                  { "@type": "HowToStep", "position": 8, "name": "Complete China Visa Application Consent Form", "text": "Download the China Visa Application Consent Form from your order confirmation email. Print and complete in black ink. Sign and include in your shipment." },
                  { "@type": "HowToStep", "position": 9, "name": "Provide Proof of Round-Trip Travel", "text": "Include one clear photocopy of round-trip airline ticket confirmation and one clear photocopy of confirmed hotel reservation from a Chinese hotel, both with your name listed." },
                  { "@type": "HowToStep", "position": 10, "name": "Obtain Letter of Invitation from China", "text": "Get an invitation letter from the Chinese company or organization you are visiting. Must be on company letterhead, signed, stamped with company seal, and explain the relationship and purpose of travel." },
                  { "@type": "HowToStep", "position": 11, "name": "Prepare Business Cover Letter from U.S. Company", "text": "Get a letter from your U.S. employer on company letterhead, signed by HR or executive. Must confirm your employment, state the purpose of your visit, and include contact information for organizations you will visit." },
                  { "@type": "HowToStep", "position": 12, "name": "Print & Include Order Confirmation", "text": "Print the PDF attached to your Fast Passport Center order confirmation email. Include it in your shipment with all other documents." },
                ],
              },
              {
                "@type": "ItemList",
                "name": "China Business Visa Required Documents",
                "description": "Complete list of 12 required documents for a China Business Visa (M-Visa) application through Fast Passport Center.",
                "numberOfItems": 12,
                "itemListElement": [
                  { "@type": "ListItem", "position": 1, "name": "COVA Online Application", "description": "Complete the COVA form online at the Chinese Consulate portal. Select M – Business/Trade visa type. Print and sign Confirmation Page." },
                  { "@type": "ListItem", "position": 2, "name": "Valid U.S. Passport & Photocopy", "description": "Submit actual passport with 6+ months validity and at least one blank visa page, plus a photocopy of the photo/data page." },
                  { "@type": "ListItem", "position": 3, "name": "Photocopy of Valid Photo ID", "description": "Color copy of front and back of Driver's License, State ID, Military ID, or Passport." },
                  { "@type": "ListItem", "position": 4, "name": "Two New Passport Photos", "description": "Two color passport photos under 6 months old, white background, no hat or glasses." },
                  { "@type": "ListItem", "position": 5, "name": "Photocopy of Most Recent China Visa", "description": "Copy of your most recent China visa if applicable. Submit old passport if visa is in it." },
                  { "@type": "ListItem", "position": 6, "name": "Non-U.S. Citizen Documentation", "description": "For non-natural-born citizens: photocopy of Naturalization Certificate, Green Card, U.S. Visa, I-20, or I-94." },
                  { "@type": "ListItem", "position": 7, "name": "Proof of Residence Form", "description": "Download from order confirmation email. Complete in black ink and sign." },
                  { "@type": "ListItem", "position": 8, "name": "China Visa Application Consent Form", "description": "Download from order confirmation email. Complete in black ink and sign." },
                  { "@type": "ListItem", "position": 9, "name": "Proof of Round-Trip Travel", "description": "Photocopy of round-trip airline ticket and confirmed Chinese hotel reservation." },
                  { "@type": "ListItem", "position": 10, "name": "Letter of Invitation from China", "description": "Invitation letter on company letterhead from the Chinese business entity, signed and stamped with company seal." },
                  { "@type": "ListItem", "position": 11, "name": "Business Cover Letter from U.S. Company", "description": "Letter on U.S. company letterhead confirming employment and purpose of China visit." },
                  { "@type": "ListItem", "position": 12, "name": "Order Confirmation Printout", "description": "Print the PDF from your Fast Passport Center order confirmation email." },
                ],
              },
              {
                "@type": "BreadcrumbList",
                "itemListElement": [
                  { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://fastpassportcenter.com/" },
                  { "@type": "ListItem", "position": 2, "name": "Passport Services", "item": "https://fastpassportcenter.com/passport-services/" },
                  { "@type": "ListItem", "position": 3, "name": "China Business Visa", "item": "https://fastpassportcenter.com/passport-services/chinese-business-visa/" },
                  { "@type": "ListItem", "position": 4, "name": "China Business Visa Checklist" },
                ],
              },
            ],
          }),
        }}
      />

      <div className="min-h-screen bg-background-50 font-sans">
        {/* ── Sticky Nav ── */}
        <nav className="sticky top-0 z-50 bg-white border-b border-background-200 print:hidden">
          <div className="max-w-3xl mx-auto px-4 md:px-6 h-14 flex items-center justify-between">
            <a
              href="/passport-services/chinese-business-visa"
              className="flex items-center gap-1.5 text-[#1A365D] text-sm font-medium hover:text-[#F6C344] transition-colors"
            >
              <i className="ri-arrow-left-line"></i>
              <span className="hidden sm:inline">Back to China Business Visa</span>
              <span className="sm:hidden">Back</span>
            </a>
            <div className="flex items-center gap-3">
              <button
                onClick={handleReset}
                className="text-xs text-foreground-400 hover:text-foreground-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                Reset All
              </button>
              <button
                onClick={handlePrint}
                className="flex items-center gap-1.5 text-xs font-semibold bg-[#1A365D] hover:bg-[#0F2240] text-white px-3 py-2 rounded-md transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-printer-line"></i> Print Checklist
              </button>
            </div>
          </div>
        </nav>

        <div className="max-w-3xl mx-auto px-4 md:px-6 py-10">

          {/* ── Page Header ── */}
          <div className="bg-[#1A365D] rounded-xl px-6 md:px-10 py-8 mb-6 print:bg-white print:border print:border-gray-300">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-2 print:text-[#1A365D]">
              Fast Passport Center
            </p>
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-3 print:text-[#1A365D]">
              China Business Visa Checklist
            </h1>
            <p className="text-white/75 text-sm leading-relaxed mb-5 print:text-gray-600">
              Follow this checklist to assemble your China Business Visa (M-Visa) application. Check off each item as you complete it. Once all items are checked, call us to verbally verify your paperwork before shipping.
            </p>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 print:hidden">
              <a
                href="tel:8772530084"
                className="inline-flex items-center gap-2 text-white/80 hover:text-[#F6C344] text-sm font-medium transition-colors"
              >
                <i className="ri-phone-line text-[#F6C344]"></i>
                Questions? Call 877-253-0084 · Mon–Fri 8AM–6PM EST
              </a>
            </div>
          </div>

          {/* ── Service Area Notice ── */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-5 py-4 mb-6 flex gap-3">
            <i className="ri-information-line text-amber-500 mt-0.5 flex-shrink-0 text-lg"></i>
            <div>
              <p className="text-amber-900 font-semibold text-sm mb-0.5">Service Area Requirement</p>
              <p className="text-amber-800 text-sm leading-relaxed">
                To expedite your China Business Visa with us, you must reside in the jurisdiction of the Chinese Consulate you apply through. We will confirm the correct consulate for your state when you place your order.
              </p>
            </div>
          </div>

          {/* ── Progress Card ── */}
          <div className="bg-white border border-background-200 rounded-xl px-5 py-5 mb-6 print:hidden">
            <div className="flex items-start justify-between gap-4 mb-3">
              <div>
                <p className="text-foreground-950 font-bold text-sm">Your Progress</p>
                <p className="text-foreground-400 text-xs">Check off each item below as you complete it.</p>
              </div>
              <div className="flex flex-col items-end">
                <span
                  className={`text-2xl font-bold tabular-nums ${
                    progressPct === 100 ? "text-[#2C7A4B]" : "text-[#1A365D]"
                  }`}
                >
                  {progressPct}%
                </span>
                <span className="text-foreground-400 text-xs">
                  {completedCount} / {ITEMS.length} items
                </span>
              </div>
            </div>
            {/* Progress bar */}
            <div className="w-full bg-background-200 rounded-full h-3 overflow-hidden">
              <div
                className={`h-3 rounded-full transition-all duration-500 ${
                  progressPct === 100 ? "bg-[#2C7A4B]" : "bg-[#1A365D]"
                }`}
                style={{ width: `${progressPct}%` }}
              ></div>
            </div>
            {progressPct === 100 && (
              <div className="mt-3 flex items-center gap-2 text-[#2C7A4B] text-sm font-semibold">
                <i className="ri-checkbox-circle-fill"></i>
                All items complete! Remember to call us before shipping.
              </div>
            )}
          </div>

          {/* ── Checklist Items ── */}
          <div className="space-y-2.5 mb-8">
            {ITEMS.map((item, i) => (
              <ChecklistItem
                key={item.number}
                number={item.number}
                title={item.title}
                tag={item.tag}
                checked={checked[i]}
                onToggle={() => handleToggle(i)}
              >
                {item.body}
              </ChecklistItem>
            ))}
          </div>

          {/* ── STOP Banner ── */}
          <div className="border-2 border-red-300 bg-red-50 rounded-xl px-6 py-6 mb-6 text-center">
            <p className="text-red-700 font-bold text-lg mb-2">
              STOP — Call Us Before Shipping
            </p>
            <p className="text-red-600 text-sm leading-relaxed mb-4">
              Place all checklist items along with your printed order confirmation inside the shipping envelope. Then call us during business hours (Monday–Friday 8AM–6PM EST) to verbally double-check your paperwork before you ship.
            </p>
            <a
              href="tel:8772530084"
              className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-bold text-sm px-6 py-3 rounded-md transition-all whitespace-nowrap"
            >
              <i className="ri-phone-line"></i> Call 877-253-0084
            </a>
          </div>

          {/* ── Shipping Address ── */}
          <div className="bg-[#1A365D] rounded-xl px-6 py-6 mb-10 text-center">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-2">
              Shipping Address
            </p>
            <p className="text-white/70 text-sm mb-3">
              Ship FedEx or UPS — 10:30 AM Delivery
            </p>
            <p className="text-white font-bold text-base leading-relaxed">
              Fast Passport Center<br />
              1629 K Street, Suite 300<br />
              Washington, DC 20006
            </p>
          </div>

          {/* ── Cross-link to Tourist Visa Checklist ── */}
          <div className="bg-accent-50 border border-accent-200 rounded-xl px-5 py-5 mb-6 text-center print:hidden">
            <p className="text-foreground-800 font-medium text-sm mb-0.5">
              Traveling for leisure instead of business?
            </p>
            <p className="text-foreground-500 text-xs mb-3">
              Switch to our <strong>China Tourist Visa (L-Visa)</strong> checklist.
            </p>
            <a
              href="/passport-services/chinese-tourist-visa-checklist"
              className="inline-flex items-center gap-1.5 text-accent-700 hover:text-accent-800 text-sm font-semibold transition-colors whitespace-nowrap"
            >
              View China Tourist Visa Checklist <i className="ri-arrow-right-line"></i>
            </a>
          </div>

          {/* ── Back link ── */}
          <div className="text-center print:hidden">
            <a
              href="/passport-services/chinese-business-visa"
              className="inline-flex items-center gap-2 text-[#1A365D] hover:text-[#F6C344] text-sm font-medium transition-colors"
            >
              <i className="ri-arrow-left-line"></i>
              Return to China Business Visa Service Page
            </a>
          </div>
        </div>
      </div>
    </>
  );
}